import { lazy, Suspense, useEffect, useState } from "react";
import { StyxAppShell } from "./app/StyxAppShell";
import { useClearSelectionOnOutsideTap } from "./hooks/useClearSelectionOnOutsideTap";

const GeneratorPage = lazy(() => import("./pages/GeneratorPage"));
const LoadingPage = lazy(() => import("./pages/LoadingPage"));
const ThemeShowcasePage = lazy(() => import("./pages/ThemeShowcasePage"));
const ThemeLabPage = lazy(() => import("./pages/ThemeLabPage"));

type RouteKind = "app" | "generator" | "loading" | "theme-ui-kit" | "theme-preview" | "theme-lab";

function getRouteKind(): RouteKind {
  const p = window.location.pathname;
  if (p === "/generator" || p.startsWith("/generator/")) return "generator";
  if (p === "/loading" || p.startsWith("/loading/")) return "loading";
  if (p === "/theme-ui-kit" || p.startsWith("/theme-ui-kit/")) return "theme-ui-kit";
  if (p === "/theme-preview" || p.startsWith("/theme-preview/")) return "theme-preview";
  if (p === "/theme-lab" || p.startsWith("/theme-lab/")) return "theme-lab";
  return "app";
}

function useRouteKind(): RouteKind {
  const [routeKind, setRouteKind] = useState<RouteKind>(() => getRouteKind());
  useEffect(() => {
    const onPop = () => setRouteKind(getRouteKind());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  return routeKind;
}

export default function App() {
  useClearSelectionOnOutsideTap();
  const routeKind = useRouteKind();

  if (routeKind === "generator") {
    return (
      <Suspense
        fallback={
          <div
            style={{
              minHeight: "100vh",
              background: "#000",
              color: "#00ff41",
              fontFamily: "IBM Plex Mono, monospace",
            }}
          />
        }
      >
        <GeneratorPage />
      </Suspense>
    );
  }
  if (routeKind === "loading") {
    return (
      <Suspense
        fallback={
          <div
            style={{
              minHeight: "100vh",
              background: "#000",
              color: "#00ff41",
              fontFamily: "IBM Plex Mono, monospace",
            }}
          />
        }
      >
        <LoadingPage />
      </Suspense>
    );
  }
  if (routeKind === "theme-ui-kit" || routeKind === "theme-preview") {
    return (
      <Suspense
        fallback={
          <div
            style={{
              minHeight: "100vh",
              background: "var(--bg, #000)",
              color: "var(--green, #00ff41)",
              fontFamily: "IBM Plex Mono, monospace",
            }}
          />
        }
      >
        <ThemeShowcasePage mode={routeKind === "theme-ui-kit" ? "kit" : "compare"} />
      </Suspense>
    );
  }
  if (routeKind === "theme-lab") {
    return (
      <Suspense
        fallback={
          <div
            style={{
              minHeight: "100vh",
              background: "var(--bg, #000)",
              color: "var(--green, #00ff41)",
              fontFamily: "IBM Plex Mono, monospace",
            }}
          />
        }
      >
        <ThemeLabPage />
      </Suspense>
    );
  }
  return <StyxAppShell />;
}
